import type { CartResponse, CartItem } from '@/types/cart';
import type { OrderResult } from '@/types/checkout';

// ─── Config accessor ──────────────────────────────────────────────────────────
function cfg() {
    return window.altcheckoutConfig?.tracking;
}

// ─── GA4 items helper ─────────────────────────────────────────────────────────
function toGA4Items(items: CartItem[]) {
    return items.map((item, index) => ({
        item_id: String(item.id),
        item_name: item.name,
        price: Number(item.prices.price) / 10 ** item.prices.currency_minor_unit,
        quantity: item.quantity,
        index,
    }));
}

// ─── Pixel fire primitives ────────────────────────────────────────────────────

/** Initialize fbq ourselves as a last resort if no other plugin has done it */
function initFbqFallback(): void {
    const config = cfg();
    if (!config?.meta_pixel_enabled || !config.meta_pixel_id) return;
    if (typeof window.fbq === 'function') return;
    const f = window as any;
    const n = (f.fbq = function (...args: unknown[]) {
        n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
    }) as any;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [] as unknown[];
    const s = document.createElement('script');
    s.async = true;
    s.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(s);
    f.fbq('init', config.meta_pixel_id);
}

export function fireMetaPixel(
    event: string,
    params: Record<string, unknown>,
    retries = 15,
    eventID?: string,
): void {
    const config = cfg();
    if (!config?.meta_pixel_enabled) return;
    if (typeof window.fbq !== 'function') {
        if (retries > 0) {
            // After 10 retries (5s), init fbq ourselves as fallback
            if (retries === 5) initFbqFallback();
            setTimeout(() => fireMetaPixel(event, params, retries - 1, eventID), 500);
        }
        return;
    }
    // A deterministic eventID (e.g. derived from the order id) lets Meta dedupe
    // repeat fires of the same event — e.g. when the thank-you page is reloaded.
    window.fbq('track', event, params, { eventID: eventID ?? config.event_id });
}

export function fireGA4(event: string, params: Record<string, unknown>, retries = 15): void {
    const config = cfg();
    if (!config?.ga4_enabled) return;
    if (typeof window.gtag !== 'function') {
        if (retries > 0) {
            setTimeout(() => fireGA4(event, params, retries - 1), 500);
        }
        return;
    }
    window.gtag('event', event, params);
}

export function fireTikTok(event: string, params: Record<string, unknown>, retries = 15): void {
    const config = cfg();
    if (!config?.tiktok_pixel_enabled) return;
    if (typeof window.ttq?.track !== 'function') {
        if (retries > 0) {
            setTimeout(() => fireTikTok(event, params, retries - 1), 500);
        }
        return;
    }
    window.ttq.track(event, params);
}

export function fireDataLayer(payload: Record<string, unknown>): void {
    const config = cfg();
    if (!config?.gtm_enabled) return;
    window.dataLayer = window.dataLayer ?? [];
    window.dataLayer.push({ ecommerce: null }); // GTM requirement: clear before new event
    window.dataLayer.push(payload);
}

// ─── Step-level orchestrators ─────────────────────────────────────────────────

export function fireCheckoutStepEvent(step: string, cart: CartResponse | null): void {
    if (!cart) return;
    const { items, totals, items_count } = cart;
    const value = Number(totals.total_price) / 10 ** totals.currency_minor_unit;
    const currency = totals.currency_code;
    const contentIds = items.map((i) => String(i.id));
    const contents = items.map((i) => ({ id: String(i.id), quantity: i.quantity }));

    if (step === 'contact') {
        // ViewContent fires when checkout page first loads (user is on contact step)
        fireMetaPixel('ViewContent', {
            content_ids: contentIds,
            content_type: 'product',
            contents,
            currency,
            value,
        });
        fireDataLayer({ event: 'view_item', ecommerce: { currency, value, items: toGA4Items(items) } });
    }

    if (step === 'billing') {
        // InitiateCheckout: user advanced past contact step
        fireMetaPixel('InitiateCheckout', {
            content_ids: contentIds,
            contents,
            currency,
            num_items: items_count,
            value,
        });
        fireGA4('begin_checkout', { currency, value, items: toGA4Items(items) });
        fireTikTok('InitiateCheckout', {
            value,
            currency,
            content_type: 'product',
            contents: items.map((i) => ({
                content_id: String(i.id),
                content_name: i.name,
                quantity: i.quantity,
                price: Number(i.prices.price) / 10 ** i.prices.currency_minor_unit,
            })),
        });
        // fireDataLayer internally prepends { ecommerce: null } before the payload
        fireDataLayer({ event: 'begin_checkout', ecommerce: { currency, value, items: toGA4Items(items) } });
    }

    if (step === 'payment') {
        // AddPaymentInfo fires when payment step opens
        fireMetaPixel('AddPaymentInfo', {
            content_ids: contentIds,
            contents,
            currency,
            value,
        });
        // GA4 add_shipping_info (billing address = shipping address in this checkout)
        fireGA4('add_shipping_info', { currency, value, shipping_tier: 'Standard', items: toGA4Items(items) });
        // GA4 add_payment_info (same moment — payment step revealed)
        fireGA4('add_payment_info', { currency, value, items: toGA4Items(items) });
        // GTM equivalents — fireDataLayer internally prepends { ecommerce: null }
        fireDataLayer({ event: 'add_shipping_info', ecommerce: { currency, value, shipping_tier: 'Standard', items: toGA4Items(items) } });
        fireDataLayer({ event: 'add_payment_info', ecommerce: { currency, value, items: toGA4Items(items) } });
    }
}

/**
 * Fire Purchase event from the thank-you page using order details from the REST API.
 * This is used when the page was loaded directly (e.g. redirect after payment)
 * and the cart data is no longer available in React Query cache.
 */
export function firePurchaseFromOrderDetails(order: {
    order_id: number;
    is_paid?: boolean;
    status?: string;
    items: Array<{ product_id: number; name: string; quantity: number; total: number }>;
    totals: { total: number };
    currency_code: string;
    currency_symbol: string;
}): void {
    // Only count a Purchase for orders that are actually paid. Offline gateways
    // (Bank Transfer / IBFT / cheque) create pending/on-hold orders with no money
    // received — firing Purchase for those inflates ad-platform conversions with
    // phantom sales. is_paid is the authoritative signal from the REST endpoint
    // ($order->is_paid()); fall back to the known paid statuses if it is absent.
    const PAID_STATUSES = ['processing', 'completed'];
    const isPaid = order.is_paid ?? (order.status ? PAID_STATUSES.includes(order.status) : false);
    if (!isPaid) return;

    const value = Number(order.totals.total) || 0;
    const currency = order.currency_code || 'USD';
    const ga4Items = order.items.map((item, index) => ({
        item_id: String(item.product_id),
        item_name: item.name,
        price: item.total,
        quantity: item.quantity,
        index,
    }));

    // NO Meta Purchase here: Pixel Manager Pro owns it (paid-gated, browser +
    // CAPI with dedup). Firing it from the SPA double-counts conversions —
    // that was the bug fixed in quick task 260619-aaa. Meta FUNNEL events
    // (ViewContent/InitiateCheckout/AddPaymentInfo) stay ours because they're
    // tied to SPA step transitions PMP cannot see.

    fireGA4('purchase', {
        transaction_id: String(order.order_id),
        value,
        currency,
        items: ga4Items,
    });

    fireTikTok('CompletePayment', {
        value,
        currency,
        order_id: String(order.order_id),
        content_type: 'product',
        contents: order.items.map((i) => ({
            content_id: String(i.product_id),
            content_name: i.name,
            quantity: i.quantity,
            price: i.total,
        })),
    });

    fireDataLayer({
        event: 'purchase',
        ecommerce: {
            transaction_id: String(order.order_id),
            currency,
            value,
            items: ga4Items,
        },
    });
}

export function fireOrderCompleteEvent(orderResult: OrderResult, cart: CartResponse | null): void {
    if (!cart) return;
    const { items, totals } = cart;
    const value = Number(totals.total_price) / 10 ** totals.currency_minor_unit;
    const currency = totals.currency_code;

    // NO Meta Purchase here: Pixel Manager Pro owns it (see comment in
    // firePurchaseFromOrderDetails above / quick task 260619-aaa).

    fireGA4('purchase', {
        transaction_id: String(orderResult.orderId),
        value,
        currency,
        items: toGA4Items(items),
    });

    fireTikTok('CompletePayment', {
        value,
        currency,
        order_id: String(orderResult.orderId),
        content_type: 'product',
        contents: items.map((i) => ({
            content_id: String(i.id),
            content_name: i.name,
            quantity: i.quantity,
            price: Number(i.prices.price) / 10 ** i.prices.currency_minor_unit,
        })),
    });

    fireDataLayer({
        event: 'purchase',
        ecommerce: {
            transaction_id: String(orderResult.orderId),
            currency,
            value,
            items: toGA4Items(items),
        },
    });
}
